import * as React from 'react';
import Lazy from 'lazy.js';
import { History } from 'history';
import { Loader } from '../../../../../../components/Loader/Loader';
import { getNameAndAges } from '../../../../../../helpers/autocomplete/autocomplete';
import { Team, TeamPlayerWithClashes } from '../../../../../../models/team';
import { SchoolEvent } from '../../../../../../models/event';
import { AppUser } from '../../../../../App/App';
import { SimpleModal } from '../../../../../../components/SimpleModal/SimpleModal';
import { ClashEventsModal } from '../../../../../../components/ClashEventsModal/ClashEventsModal';
import moment from 'moment';
import propz from 'propz';
import {
  getEventClash,
  getClashEventDisplayTitle,
  getPrototypeTeamPlayersMethod,
  getPositionNameById,
  isClubEvent
} from '../../../../../../helpers/event/event';
import { getSchoolEvent } from '../../../../../../helpers/service/admin/event';
import BPromise from 'bluebird';
import { getFromHistory } from '../../../../../../helpers/history/history';
import { TEAM_TYPE } from '../../../../../../consts/team';
import { removeSchoolEventTeam } from '../../../../../../helpers/service/admin/schoolEventTeams';
import { addSchoolEventTeam } from '../../../../../../helpers/service/admin/tournamentTeams';
import { SchoolEventClubEventRadioButtons } from '../SchoolEventClubEventRadioButtons/SchoolEventClubEventRadioButtons';
import { CLUB_EVENT_EDIT_MODE } from '../../../../../../consts/event';
import { addTeamInClubEvents } from '../../../../../../helpers/service/admin/clubTeams';
import { getSchoolTeam } from '../../../../../../helpers/service/admin/schoolTeams';
import { getEventStartDateAndTime } from '../../../../../../helpers/event/schoolEventViewHelper';
import '../SchoolEventManager.scss';
import '../SchoolEventAddExistingTeam/SchoolEventAddExistingTeam.scss';
import './SchoolEventUpdatingTeamManager.scss';

interface Props {
  user: AppUser;
  history: History;
}

interface State {
  players: TeamPlayerWithClashes[];
  isLoading: boolean;
  isError: boolean;
  playerEventsModalText: string;
  isPlayerEventsModalOpen: boolean;
  isClubEventsModalOpen: boolean;
  event: SchoolEvent;
  team: Team;
  editMode: string;
  errorMessage: string;
}

export class SchoolEventUpdatingTeamManager extends React.Component<Props, State> {
  private isSaveInProgress = false;

  constructor(props: Props) {
    super(props);

    this.state = {
      team: undefined,
      players: [],
      isLoading: true,
      isError: false,
      playerEventsModalText: '',
      isPlayerEventsModalOpen: false,
      isClubEventsModalOpen: false,
      event: undefined,
      editMode: CLUB_EVENT_EDIT_MODE.SINGLE,
      errorMessage: ''
    };
  }

  getTeamId(): string {
    const { history } = this.props;
    return getFromHistory(history, 'team');
  }

  getCloneOf(): string {
    const { history } = this.props;
    return getFromHistory(history, 'clone');
  }

  getEventId(): string {
    const { history } = this.props;
    return getFromHistory(history, 'event');
  }

  componentDidMount() {
    this.setState({
      isLoading: true
    });

    const { user } = this.props;
    const eventId = this.getEventId();

    const eventPromise = getSchoolEvent(user, eventId);

    BPromise.all([eventPromise])
      .then(([event]) => {
        this.setState({
          event: event
        });

        const teamSourceOfCloneId = this.getCloneOf();
        return getSchoolTeam(user, teamSourceOfCloneId);
      })
      .then(updatedTeam => {
        return this.onSelectTeam(updatedTeam, this.state.event);
      })
      .catch(() =>
        this.setState({
          isLoading: false,
          isError: true,
          errorMessage: 'The source team could not be loaded. Return to the event and try again.'
        })
      );
  }

  onClosePlayerEventsModalClick = () => {
    this.setState({
      isPlayerEventsModalOpen: false
    });
  };

  renderPlayerEventsModal(): React.ReactNode {
    const { isPlayerEventsModalOpen, playerEventsModalText } = this.state;
    return (
      <ClashEventsModal
        isOpen={isPlayerEventsModalOpen}
        text={playerEventsModalText}
        onClose={this.onClosePlayerEventsModalClick}
      />
    );
  }

  onEditModeClick = editMode => {
    this.setState({
      editMode: editMode
    });
  };

  onCloseClubEventsModal = () => {
    this.setState({
      isClubEventsModalOpen: false
    });
  };

  onClubEventsSaveClick = () => {
    if (this.isSaveInProgress) return;

    const { team, editMode, event } = this.state;
    const { user, history } = this.props;
    const eventId = this.getEventId();
    const teamId = this.getTeamId();

    const isEditModeSingle = editMode === CLUB_EVENT_EDIT_MODE.SINGLE;

    this.isSaveInProgress = true;
    this.setState({
      isLoading: true
    });

    const data = { ...team, cloneOf: team.id, teamType: TEAM_TYPE.CLONE };

    const savePromise = isEditModeSingle
      ? removeSchoolEventTeam(user, eventId, teamId).then(res => {
          return addSchoolEventTeam(user, eventId, data);
        })
      : addTeamInClubEvents(user, event.clubId, eventId, teamId, data);

    savePromise
      .then(res => {
        history.push({
          pathname: '/events/event',
          search: `id=${eventId}`
        });
      })
      .catch(error => {
        this.isSaveInProgress = false;
        this.setState({
          isLoading: false,
          isError: true,
          isClubEventsModalOpen: false,
          errorMessage:
            propz.get(error, ['response', 'data', 'details', 'text'], '') ||
            'The team could not be updated. Please try again.'
        });
      });
  };

  renderClubEventsModal(): React.ReactNode {
    const { isClubEventsModalOpen, editMode } = this.state;
    return (
      <SimpleModal
        isOpen={isClubEventsModalOpen}
        onCloseClick={this.onCloseClubEventsModal}
        onBackdropClick={this.onCloseClubEventsModal}
        showHeader={false}
        showFooter={false}
        isBodyBold={false}
        isBare
        dialogStyle={{ maxWidth: 440 }}
        customClass="eUpdateTeamScopeModal"
        zIndex={1800}
      >
        <section
          className="eClubScopeModal__dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="update-team-scope-title"
        >
          <header>
            <span className="eClubScopeModal__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path d="M7 7h10v10H7zM4 4h10M4 4v10M10 20h10V10" />
              </svg>
            </span>
            <div>
              <h2 id="update-team-scope-title">Update club team</h2>
              <p>Choose which events should use the latest roster from this saved team.</p>
            </div>
            <button type="button" aria-label="Close" onClick={this.onCloseClubEventsModal}>
              ×
            </button>
          </header>
          <SchoolEventClubEventRadioButtons onEditModeClick={this.onEditModeClick} editMode={editMode} />
          <p className="eUpdateTeamScopeModal__note">
            This replaces the event roster, including any players added or removed separately.
          </p>
          <footer>
            <button type="button" onClick={this.onCloseClubEventsModal}>
              Keep reviewing
            </button>
            <button type="button" className="is-primary" onClick={this.onClubEventsSaveClick}>
              Update team
            </button>
          </footer>
        </section>
      </SimpleModal>
    );
  }

  onSelectTeam = (team, event) => {
    const { user } = this.props;
    this.setState({
      isLoading: true
    });

    const { startTime, endTime } = event;
    const { id } = team;

    let where = {};

    // copy-paste from old frontend
    const startTimeCopy = new Date(startTime);
    const startTimeDate = new Date(startTime);
    startTimeCopy.setHours(startTimeDate.getHours() - 1);
    const intervalClashStartDate = moment(startTimeCopy)
      .utc()
      .format();

    const endTimeCopy = new Date(endTime);
    const endTimeDate = new Date(endTime);
    endTimeCopy.setHours(endTimeDate.getHours() + 1);
    const intervalClashEndDate = moment(endTimeCopy)
      .utc()
      .format();

    propz.set(where, ['intervalClashStartDate'], intervalClashStartDate);
    propz.set(where, ['intervalClashEndDate'], intervalClashEndDate);
    propz.set(where, ['fullClashStartDate'], startTime);
    propz.set(where, ['fullClashEndDate'], endTime);

    return getPrototypeTeamPlayersMethod(user)(user, id, { where }).then(players => {
      this.setState({
        team,
        players,
        isLoading: false,
        isError: false
      });
    });
  };

  onClashClick = (player: TeamPlayerWithClashes) => {
    const { user } = this.props;
    const { clashes, firstName, lastName } = player;
    const { fullClashEvents, intervalClashEvents } = clashes;
    const fullClashEventIds = fullClashEvents.map(fullClashEvent => fullClashEvent._id);
    const intervalClashEventIds = intervalClashEvents.map(intervalClashEvent => intervalClashEvent._id);

    const allPlayerEventIds = [...fullClashEventIds, ...intervalClashEventIds];
    const allPlayerEventIdsUniq = Lazy(allPlayerEventIds)
      .uniq()
      .toArray();

    this.setState({
      isLoading: true
    });

    const eventsPromise: Promise<SchoolEvent>[] = allPlayerEventIdsUniq.map(eventId => getSchoolEvent(user, eventId));

    BPromise.all(eventsPromise).then(events => {
      const eventNames = events
        .map((event, index) => {
          const title = getClashEventDisplayTitle(event, user);
          const clash = getEventClash(event, fullClashEventIds, intervalClashEventIds);
          return `${index + 1}. ${title} ${clash}`;
        })
        .join('\n ');

      this.setState({
        isLoading: false,
        playerEventsModalText: `${firstName} ${lastName} is participating in the following events: \n${eventNames}`,
        isPlayerEventsModalOpen: true
      });
    });
  };

  onCancelClick = () => {
    const { history } = this.props;
    const eventId = this.getEventId();

    history.push({
      pathname: '/events/event',
      search: `id=${eventId}`
    });
  };

  onSaveClick = () => {
    const { team, event } = this.state;
    const { user, history } = this.props;
    const eventId = this.getEventId();
    const teamId = this.getTeamId();
    const isTeamExist = typeof team !== 'undefined';
    if (isTeamExist) {
      if (this.isSaveInProgress) return;

      if (isClubEvent(event)) {
        this.setState({
          isClubEventsModalOpen: true
        });
      } else {
        this.isSaveInProgress = true;
        this.setState({
          isLoading: true
        });

        const data = { ...team, cloneOf: team.id, teamType: TEAM_TYPE.CLONE };

        removeSchoolEventTeam(user, eventId, teamId)
          .then(res => {
            return addSchoolEventTeam(user, eventId, data);
          })
          .then(res => {
            history.push({
              pathname: '/events/event',
              search: `id=${eventId}`
            });
          })
          .catch(error => {
            this.isSaveInProgress = false;
            this.setState({
              isLoading: false,
              isError: true,
              errorMessage:
                propz.get(error, ['response', 'data', 'details', 'text'], '') ||
                'The team could not be updated. Please try again.'
            });
          });
      }
    } else {
      this.setState({
        isError: true
      });
    }
  };

  render() {
    const {
      isLoading,
      players,
      isError,
      isPlayerEventsModalOpen,
      isClubEventsModalOpen,
      team,
      event: schoolEvent,
      errorMessage
    } = this.state;
    const { user } = this.props;

    if (isLoading) {
      return <Loader />;
    }

    const { activeSchool } = user;
    const { isShowStudentEventClashes } = activeSchool;
    const playersLabel = `${players.length} ${players.length === 1 ? 'player' : 'players'}`;

    const classes = `eSchoolEventUpdateTeam${isPlayerEventsModalOpen || isClubEventsModalOpen ? ' modal-open' : ''}`;

    return (
      <div className={classes}>
        {this.renderPlayerEventsModal()}
        {this.renderClubEventsModal()}

        <section className="eSchoolEventAddExistingTeam__buildCard">
          <header className="eSchoolEventAddExistingTeam__buildHead">
            <span className="eSchoolEventAddExistingTeam__buildIcon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22">
                <path d="M20 7v5h-5M4 17v-5h5M6 8a7 7 0 0 1 12-2l2 2M18 16a7 7 0 0 1-12 2l-2-2" />
              </svg>
            </span>
            <div className="eSchoolEventAddExistingTeam__headCopy">
              <h1 className="eSchoolEventAddExistingTeam__title">Update team</h1>
              <p className="eSchoolEventAddExistingTeam__subtitle">
                Review the latest saved roster before applying it to the event.
              </p>
            </div>
          </header>
          <div className="eSchoolEventAddExistingTeam__divider" />
          <div className="eSchoolEventUpdateTeam__summary">
            <div>
              <span>Saved team</span>
              <strong>{team ? getNameAndAges(team) : 'Unavailable'}</strong>
            </div>
            <div>
              <span>Event</span>
              <strong>{schoolEvent ? getEventStartDateAndTime(schoolEvent) : 'Unavailable'}</strong>
            </div>
            <div>
              <span>Latest roster</span>
              <strong>{playersLabel}</strong>
            </div>
          </div>
        </section>
        {isError && (
          <div className="eSchoolEventUpdateTeam__error" role="alert">
            {errorMessage || 'The team cannot be found.'}
          </div>
        )}
        {team && (
          <section className="eSchoolEventAddExistingTeam__buildCard">
            <header className="eSchoolEventUpdateTeam__rosterHead">
              <div>
                <h2>Team roster</h2>
                <p>The current event roster will be replaced with these players.</p>
              </div>
              <span>{playersLabel}</span>
            </header>
            <div className="eSchoolEventUpdateTeam__tableWrap">
              <table className="eSchoolEventUpdateTeam__table">
                <thead>
                  <tr>
                    <th>Player</th>
                    <th>Position</th>
                    <th>Form</th>
                    <th>House</th>
                    {isShowStudentEventClashes && <th>Availability</th>}
                  </tr>
                </thead>
                <tbody>
                  {players.map(player => {
                    const fullClash = propz.get<any[]>(player, ['clashes', 'fullClashEvents'], []).length > 0;
                    const intervalClash = propz.get<any[]>(player, ['clashes', 'intervalClashEvents'], []).length > 0;
                    const positionId = propz.get(player, ['positionId'], '');
                    return (
                      <tr key={`${player.userId}:${player.permissionId}`}>
                        <td>
                          <div className="eSchoolEventUpdateTeam__person">
                            <span className="eSchoolEventUpdateTeam__avatar" aria-hidden="true">{`${(
                              player.firstName || ''
                            ).charAt(0)}${(player.lastName || '').charAt(0)}`}</span>
                            <strong>
                              {player.firstName} {player.lastName}
                            </strong>
                          </div>
                        </td>
                        <td>{positionId && schoolEvent ? getPositionNameById(positionId, schoolEvent) || '—' : '—'}</td>
                        <td>{propz.get(player, ['form', 'name'], '') || '—'}</td>
                        <td>{propz.get(player, ['house', 'name'], '') || '—'}</td>
                        {isShowStudentEventClashes && (
                          <td>
                            {fullClash || intervalClash ? (
                              <button
                                type="button"
                                className={`eSchoolEventUpdateTeam__clash is-${fullClash ? 'full' : 'possible'}`}
                                onClick={() => this.onClashClick(player)}
                              >
                                {fullClash ? 'Clash' : 'Possible clash'}
                              </button>
                            ) : (
                              <span className="eSchoolEventUpdateTeam__free">Free</span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                  {!players.length && (
                    <tr>
                      <td className="eSchoolEventUpdateTeam__empty" colSpan={isShowStudentEventClashes ? 5 : 4}>
                        This saved team has no players. Updating will clear the event roster.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}
        <footer className="bSchoolTeamManager__footer">
          <div className="bSchoolTeamManager__footerLeft">{playersLabel} in the latest roster</div>
          <div className="bSchoolTeamManager__footerRight">
            <button
              type="button"
              className="bSchoolTeamManager__btn bSchoolTeamManager__btnSecondary"
              onClick={this.onCancelClick}
            >
              Cancel
            </button>
            <button
              type="button"
              className="bSchoolTeamManager__btn bSchoolTeamManager__btnPrimary"
              disabled={!team || this.isSaveInProgress}
              onClick={this.onSaveClick}
            >
              Update team
            </button>
          </div>
        </footer>
      </div>
    );
  }
}
